<template>
  <div v-if="model.track" class="batch-timeline mb-5">
    <div class="timeline-scroll">
      <div class="timeline-plot">
        <div class="ruler">
          <span
            v-for="tick in ticks"
            :key="tick.key"
            class="ruler-tick"
            :class="`ruler-tick--${tick.anchor}`"
            :style="{ left: tick.left }"
          >
            {{ tick.label }}
          </span>

          <span
            v-if="nowFraction !== null"
            class="ruler-now"
            :style="labelStyle(nowFraction)"
          >agora</span>

          <!-- After the others, so it wins the overlap when you hover near one. -->
          <span
            v-if="cursorFraction !== null"
            class="ruler-cursor"
            :class="{ 'ruler-cursor--snapped': snapped }"
            :style="labelStyle(cursorFraction)"
          >
            {{ cursorLabel }}
          </span>
        </div>

        <div class="lanes" @mousemove="trackCursor" @mouseleave="clearCursor">
          <div ref="overlayEl" class="overlay">
            <div
              v-for="(gap, index) in gapStyles"
              :key="`gap-${index}`"
              class="gap"
              :style="gap"
            />
          </div>

          <div
            v-for="bar in model.bars"
            :key="bar.batch.id"
            class="lane"
            :class="{ 'lane--editing': bar.batch.id === editingBatchId }"
            :style="{ '--bar-rgb': `var(--v-theme-${themeColor(bar.batch)})` }"
          >
            <span class="lane-label">L{{ bar.batch.number }}</span>

            <div class="lane-track">
              <div v-if="bar.ghost" class="ghost" :style="ghostStyle(bar)" />

              <button
                type="button"
                class="bar"
                :class="{
                  'bar--open': bar.openEnded,
                  'bar--future': bar.future,
                }"
                :style="barStyle(bar)"
                @click="emit('select', bar.batch)"
              >
                <span class="bar-label">{{ soldLabel(bar.batch) }}</span>

                <!-- The period, because that is what the bar draws, plus what
                     the table below does not carry. Opening downwards keeps the
                     ruler readable. -->
                <v-tooltip activator="parent" location="bottom" open-delay="400">
                  <div class="text-caption">
                    <div v-if="bar.batch.starts_at">
                      {{ formatDate(bar.batch.starts_at) }} → {{ formatDate(bar.batch.ends_at) }}
                    </div>
                    <div>Vendidos: {{ bar.batch.sold ?? 0 }} · Reservados: {{ bar.batch.reserved ?? 0 }}</div>
                    <!-- Set only on a sell-out, and it is why the bar stops short of its ends_at. -->
                    <div v-if="bar.batch.advanced_at">
                      Virou em: {{ formatDate(bar.batch.advanced_at) }}
                    </div>
                  </div>
                </v-tooltip>
              </button>

              <span v-if="bar.openEnded" class="open-arrow" />
            </div>
          </div>

          <!-- Above the bars, so neither line is lost behind one. -->
          <div class="markers">
            <div v-if="nowFraction !== null" class="now" :style="{ left: markerLeft(nowFraction) }" />
            <div
              v-if="cursorFraction !== null"
              class="cursor-line"
              :class="{ 'cursor-line--snapped': snapped }"
              :style="{ left: markerLeft(cursorFraction) }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import { DateTime } from 'luxon'
import { formatDate, statusColor } from '@/helpers/batches'
import { buildTimeline, positionIn, spanStyle } from '@/helpers/batches/timeline'
import type { TimelineBar } from '@/helpers/batches/timeline'
import type Event from '@/types/Event'
import type TicketBatch from '@/types/TicketBatch'

defineOptions({ name: 'BatchChannelTimeline' })

const props = defineProps<{
  batches: TicketBatch[]
  event: Event
  editingBatchId: string | null
}>()

const emit = defineEmits<{
  select: [batch: TicketBatch]
}>()

// Nothing writes to the batches, so only the clock moves the drawing.
const now = ref<DateTime>(DateTime.now())
const clock = window.setInterval(() => {
  now.value = DateTime.now()
}, 60_000)

onUnmounted(() => window.clearInterval(clock))

const model = computed(() => buildTimeline(props.batches, props.event, now.value))

const TICK_FRACTIONS = [0, 0.25, 0.5, 0.75, 1]
const FINE_SCALE_MS = 3 * 24 * 60 * 60 * 1000

const ticks = computed(() => {
  const { track } = model.value

  if (!track) return []

  const total = track.end.toMillis() - track.start.toMillis()
  const format = total <= FINE_SCALE_MS ? 'dd/MM HH:mm' : 'dd/MM'

  return TICK_FRACTIONS.map(fraction => ({
    key: fraction,
    label: track.start.plus({ milliseconds: total * fraction }).toFormat(format),
    left: `${fraction * 100}%`,
    anchor: fraction === 0 ? 'start' : fraction === 1 ? 'end' : 'middle',
  }))
})

const nowFraction = computed<number | null>(() => {
  const { track } = model.value

  if (!track || now.value < track.start || now.value > track.end) return null

  return positionIn(track, now.value) / 100
})

function markerLeft(fraction: number): string {
  return `${fraction * 100}%`
}

/**
 * Ruler label placement. `--pos` lets the stylesheet keep the label centred on
 * its line except where that would overflow an end of the track.
 */
function labelStyle(fraction: number): Record<string, string> {
  return { 'left': markerLeft(fraction), '--pos': `${fraction}` }
}

// --- Cursor ---

/** Desktop only: a crosshair is dead weight without a hovering pointer. */
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches

/** How close the pointer must come to a boundary to land on it. */
const SNAP_PX = 7

const overlayEl = ref<HTMLElement | null>(null)
/** Where the cursor sits on the track, 0 to 1. Null when it is off the track. */
const cursor = ref<number | null>(null)
const snapped = ref<boolean>(false)

/**
 * Bar boundaries and now, as track fractions: the instants worth landing on
 * exactly. Ghost edges are left out deliberately, to keep the line from feeling
 * sticky where a configured period and a real one nearly coincide.
 */
const snapPoints = computed<number[]>(() => {
  const { track, bars } = model.value

  if (!track) return []

  const instants = [...bars.flatMap(bar => [bar.start, bar.end]), now.value]

  return [ ...new Set(
    instants
      .filter((at): at is DateTime => at !== null && at >= track.start && at <= track.end)
      .map(at => positionIn(track, at) / 100),
  ) ]
})

function trackCursor(event: MouseEvent): void {
  const element = overlayEl.value

  if (!finePointer || !element || !model.value.track) return

  // The overlay spans exactly the track, and both figures are viewport-relative,
  // so the wrapper's horizontal scroll cancels out.
  const { left, width } = element.getBoundingClientRect()

  if (width <= 0) return

  const fraction = (event.clientX - left) / width

  if (fraction < 0 || fraction > 1) {
    clearCursor()

    return
  }

  const nearest = nearestSnapPoint(fraction, SNAP_PX / width)

  snapped.value = nearest !== null
  cursor.value = nearest ?? fraction
}

function nearestSnapPoint(fraction: number, tolerance: number): number | null {
  let best: number | null = null
  let shortest = tolerance

  for (const point of snapPoints.value) {
    const distance = Math.abs(point - fraction)

    if (distance <= shortest) {
      best = point
      shortest = distance
    }
  }

  return best
}

function clearCursor(): void {
  cursor.value = null
  snapped.value = false
}

const cursorFraction = computed<number | null>(() =>
  model.value.track ? cursor.value : null,
)

const cursorLabel = computed<string>(() => {
  const { track } = model.value

  if (!track || cursor.value === null) return ''

  const total = track.end.toMillis() - track.start.toMillis()

  // Rounded because a snapped point round-trips through a fraction; without it
  // an exact 14:00 boundary can land on 13:59:59.999 and read a minute early.
  const offset = Math.round(total * cursor.value)

  return track.start.plus({ milliseconds: offset }).toFormat('dd/MM HH:mm')
})

const gapStyles = computed(() => {
  const { track, gaps } = model.value

  if (!track) return []

  return gaps.map(gap => spanStyle(track, gap.start, gap.end))
})

function themeColor(batch: TicketBatch): string {
  const color = statusColor(batch)

  return color === 'default' ? 'on-surface' : color
}

function barStyle(bar: TimelineBar): Record<string, string> {
  const { track } = model.value

  if (!track) return {}

  return spanStyle(track, bar.start, bar.end ?? track.end)
}

function ghostStyle(bar: TimelineBar): Record<string, string> {
  const { track } = model.value

  if (!track || !bar.ghost) return {}

  return spanStyle(track, bar.ghost.start, bar.ghost.end)
}

/**
 * Sales against the batch's own ceiling. Sold only, never sold + held: holds are
 * transient and the advance keys on sales. The tooltip carries both separately.
 */
function soldLabel(batch: TicketBatch): string {
  const sold = batch.sold ?? 0

  return batch.quantity === null ? `${sold}` : `${sold}/${batch.quantity}`
}
</script>

<style scoped>
.batch-timeline {
  --label-w: 38px;
  --gutter: 4px;
  /* Where the lane grid's second column starts. Every layer positioned by
     percentage must share this origin, or it drifts from the bars. */
  --track-left: calc(var(--label-w) + var(--gutter));
  --lane-h: 22px;
}

.timeline-scroll {
  overflow-x: auto;
}

.timeline-plot {
  min-width: 640px;
}

.ruler {
  container-type: inline-size;
  position: relative;
  height: 16px;
  margin-left: var(--track-left);
}

.ruler-tick {
  position: absolute;
  font-size: 0.6rem;
  white-space: nowrap;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.ruler-tick--middle {
  transform: translateX(-50%);
}

.ruler-tick--end {
  transform: translateX(-100%);
}

/* Opaque so each occludes any tick, or each other, where they land together. */
.ruler-now,
.ruler-cursor {
  position: absolute;
  padding: 0 3px;
  font-size: 0.6rem;
  font-variant-numeric: tabular-nums;
  color: rgb(var(--v-theme-on-surface));
  white-space: nowrap;
  background: rgb(var(--v-theme-surface));
  border-radius: 2px;
  /* Centred on its line, and only pushed off centre when that would overflow an
     end of the track. `%` resolves against the label, `cqw` against the ruler,
     so clamp() can hold the label's own edges inside the track. */
  transform: translateX(clamp(
    calc(var(--pos, 0.5) * -100cqw),
    -50%,
    calc((1 - var(--pos, 0.5)) * 100cqw - 100%)
  ));
}

.ruler-now {
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
}

.ruler-cursor--snapped {
  font-weight: 600;
}

.lanes {
  position: relative;
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--track-left);
  pointer-events: none;
}

.gap {
  position: absolute;
  top: 0;
  bottom: 0;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.05);
  border-radius: 3px 0 0 3px;
  background: repeating-linear-gradient(
    45deg,
    transparent 0 5px,
    rgba(var(--v-theme-on-surface), 0.05) 5px 10px
  );
}

.markers {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: var(--track-left);
  z-index: 2;
  pointer-events: none;
}

/* Solid and coloured: a real instant, and the one fixed reference on the track. */
.now {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px solid rgb(var(--v-theme-primary));
}

/* Dashed and neutral: transient, and never mistaken for the marker above. */
.cursor-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px dashed rgba(var(--v-theme-on-surface), 0.5);
}

/* Solid once it lands on a boundary: an exact time, not an interpolated one. */
.cursor-line--snapped {
  border-left-style: solid;
  border-left-color: rgba(var(--v-theme-on-surface), 0.85);
}

.lane {
  display: grid;
  grid-template-columns: var(--label-w) 1fr;
  align-items: center;
  column-gap: var(--gutter);
  padding: 2px 0;
}

.lane--editing {
  background: rgba(var(--v-theme-info), 0.05);
}

.lane-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(var(--v-theme-on-surface), 0.5);
}

.lane-track {
  position: relative;
  height: var(--lane-h);
}

.ghost {
  position: absolute;
  top: 2px;
  bottom: 2px;
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.3);
  border-radius: 3px;
}

.bar {
  container-type: inline-size;
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 12px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  background: rgba(var(--bar-rgb), 0.2);
  border: 1px solid rgb(var(--bar-rgb));
  border-radius: 3px;
  transition: filter 0.1s;
}

.bar:hover {
  filter: brightness(1.15);
}

.bar:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.bar--future {
  opacity: 0.55;
}

.bar--open {
  border-right: none;
  border-radius: 3px 0 0 3px;
  mask-image: linear-gradient(to right, #000 65%, transparent 100%);
}

.open-arrow {
  position: absolute;
  top: 50%;
  right: 0;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 7px solid rgb(var(--bar-rgb));
  transform: translateY(-50%);
}

.bar-label {
  padding: 0 3px;
  font-size: 0.6rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  /* Saturated status colour on its own tint, matching the table's chips. */
  color: rgb(var(--bar-rgb));
  white-space: nowrap;
}

/* Narrow bars cannot hold the figure without clipping it; the tooltip has it. */
@container (max-width: 55px) {
  .bar-label {
    display: none;
  }
}
</style>
